import { Typography, Badge } from '@lucid/lego';
import type { Prospect } from '../../../../api/services/lupp/prospect/dto.prospect';

export const getStatusBadgeColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'open':
      return 'informative';
    case 'contacting':
      return 'warning';
    case 'engaged':
      return 'success';
    case 'converted':
    case 'closed':
      return 'neutral';
    default:
      return 'neutral';
  }
};

export const getPriorityBadgeColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'error';
    case 'medium':
      return 'informative';
    case 'low':
      return 'warning';
    default:
      return 'neutral';
  }
};

export const getSourceDisplayName = (sourceName: string) => {
  switch (sourceName?.toLowerCase()) {
    case 'walk_in':
      return 'Walk-in';
    case 'website':
      return 'Website';
    case 'referral':
      return 'Referral';
    case 'social_media':
      return 'Social Media';
    case 'phone_call':
      return 'Phone Call';
    default:
      return sourceName;
  }
};

export const getInterestedModelDisplayName = (model: string) => {
  switch (model?.toLowerCase()) {
    case 'lucid-air':
      return 'Lucid Air';
    case 'lucid-gravity':
      return 'Lucid Gravity';
    default:
      return model;
  }
};

interface FieldType {
  label: string;
  key: string;
  value: string | number | boolean | null | string[];
}

interface SectionType {
  label: string;
  fields: FieldType[];
}

export interface ProspectDetailType {
  prospectIdentifier: string;
  prospectName: string;
  situation?: SectionType;
  prospectStatusProgression?: SectionType;
  purchaseProfile?: SectionType;
  keyFields?: SectionType;
  information?: SectionType;
}

interface CreateColumnsParams {
  theme: 'light' | 'dark';
  onEditClick: (record: Prospect) => void;
  labels: Record<string, string>;
}

export const createColumns = ({ theme, onEditClick, labels }: CreateColumnsParams) => [
  {
    key: 'statusName',
    title: labels.action || 'Action',
    width: '100px',
    align: 'center' as const,
    sortable: true,
    render: (_: unknown, record: Prospect) => {
      const status = record.statusName?.toLowerCase() || 'open';
      return (
        <Badge
          label={status.charAt(0).toUpperCase() + status.slice(1)}
          color={getStatusBadgeColor(status)}
          type="secondary"
          theme={theme === 'dark' ? 'light' : 'dark'}
          minWidth="60px"
        />
      );
    },
  },
  {
    key: 'prospectFirstName',
    title: labels.full_name || 'Full Name',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography
        variant="body"
        size="small"
        theme={theme}
        color="blue"
        onClick={() => onEditClick(record)}
      >
        {`${record.prospectFirstName || ''} ${record.prospectLastName || ''}`.trim() || '-'}
      </Typography>
    ),
  },
  {
    key: 'prospectEmail',
    title: labels.email || 'Email',
    width: '200px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.prospectEmail || '-'}
      </Typography>
    ),
  },
  {
    key: 'prospectPhone',
    title: labels.phone || 'Phone',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.prospectPhone || '-'}
      </Typography>
    ),
  },
  {
    key: 'zipCode',
    title: labels.postal_code || 'Postal Code',
    width: '120px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.zipCode || '-'}
      </Typography>
    ),
  },
  {
    key: 'city',
    title: labels.city_region || 'City/Region',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.city || record.region || '-'}
      </Typography>
    ),
  },
  {
    key: 'prospectSourceName',
    title: labels.source_of_prospect || 'Source of Prospect',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {getSourceDisplayName(record.prospectSourceName || '') || '-'}
      </Typography>
    ),
  },
  {
    key: 'nextEngagementDate',
    title: labels.next_engagement_date || 'Next Engagement Date',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Prospect) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.nextEngagementDate
          ? new Date(record.nextEngagementDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '-'}
      </Typography>
    ),
  },
];

export const createProspectDetail = (
  record: Prospect,
  labels: Record<string, string> = {}
): ProspectDetailType => ({
  prospectIdentifier: String(record.prospectIdentifier),
  prospectName: `${record.prospectFirstName || '-'} ${record.prospectLastName || '-'}`,
  situation: {
    label: labels.situation,
    fields: [
      {
        label: labels.situational_notes,
        key: 'noteText',
        value: record.noteText || labels.default_placeholder,
      },
      {
        label: labels.objections,
        key: 'objections',
        value: record.objections || labels.default_placeholder,
      },
      {
        label: labels.important_features,
        key: 'importantFeatures',
        value: record.importantFeatures || labels.default_placeholder,
      },
    ],
  },
  purchaseProfile: {
    label: labels.purchase_profile,
    fields: [
      {
        label: labels.interested_model,
        key: 'interestedModel',
        value: record.interestedModel || (labels.default_placeholder as string),
      },
      {
        label: labels.interested_trim,
        key: 'interestedTrim',
        value: record.interestedTrim || (labels.default_placeholder as string),
      },
      {
        label: labels.purchase_type,
        key: 'purchaseType',
        value: record.purchaseType || (labels.default_placeholder as string),
      },
      {
        label: labels.purchase_timing,
        key: 'purchaseTiming',
        value: record.purchaseTiming || (labels.default_placeholder as string),
      },
      {
        label: labels.finance_type,
        key: 'financeType',
        value: record.financeType || (labels.default_placeholder as string),
      },
    ],
  },
  information: {
    label: labels.information,
    fields: [
      {
        label: labels.company_name,
        key: 'companyName',
        value: record.prospectCompanyName || labels.default_placeholder,
      },
      {
        label: labels.preferred_contact_method,
        key: 'preferredContactMethod',
        value: record.preferredContactMethod || labels.default_placeholder,
      },
      {
        label: labels.preferred_language,
        key: 'preferredLanguageIso',
        value: record.preferredLanguageIso || labels.default_placeholder,
      },
    ],
  },
});
